import type { ReactNode } from 'react';
import { labelPod, type Sighting } from '@whaletail/shared';
import { clockTime, displayFor, trackColor } from '../api.ts';
import { useColorScheme } from '../basemap.ts';

/** A Facebook group where people post sightings that never reach Acartia or Orca Network. */
const COMMUNITY_REPORTS_URL = 'https://www.facebook.com/groups/564701038927716';

interface Props {
  rows: Array<Sighting & { trackId: string }>;
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Shown when there are no rows — usually a hint plus a way out. */
  empty: ReactNode;
  /** e.g. "12h" or "Mon, Sep 21", for the header count. */
  windowLabel: string;
}

export function ReportLog({ rows, loading, selectedId, onSelect, empty, windowLabel }: Props) {
  const dark = useColorScheme() === 'dark';
  return (
    <div className="log">
      <div className="log-head">
        <h2>Report Log</h2>
        <span className="count">
          {rows.length} {windowLabel}
        </span>
      </div>

      {/* Right under the heading, whatever the log holds: the group is often
          more up to date than the feeds, so it is always worth a look. */}
      <p className="log-more">
        Additional community reports may be found{' '}
        <a href={COMMUNITY_REPORTS_URL} target="_blank" rel="noopener noreferrer">
          here
        </a>
        .
      </p>

      {loading && <div className="empty">Loading sightings…</div>}
      {!loading && rows.length === 0 && <div className="empty">{empty}</div>}

      {rows.map((s) => {
        const d = displayFor(s.species);
        return (
          <button key={s.id} className="log-item" onClick={() => onSelect(s.id)} aria-current={selectedId === s.id}>
            {/* Shape still encodes species; color matches the track's map
                color (trackColor) so a log entry's dot is findable on the map
                by color, not just by clicking through to it. */}
            <span className="mark" data-shape={d.shape} style={{ background: trackColor(s.trackId, dark) }} />
            <span>
              <span className="top">
                <span className="species">{d.short}</span>
                {s.count ? <span className="badge">{s.count}</span> : null}
                {s.heading ? <span className="badge">{s.heading}</span> : null}
                {s.behavior ? <span className="badge">{s.behavior}</span> : null}
                <span className="time">{clockTime(s.observedAt)}</span>
              </span>
              <span className="place">{s.placeLabel}</span>
              {s.comments && <div className="comment">{s.comments}</div>}
              {(s.pods.length > 0 || s.observers.length > 0) && (
                <div className="meta">
                  {s.pods.length > 0 && <>{s.pods.map(labelPod).join(', ')} &middot; </>}
                  {s.observers.length > 0 && <>Reported by {s.observers.join(', ')}</>}
                </div>
              )}
            </span>
          </button>
        );
      })}

      <div className="attribution">
        Community-sourced and unofficial — <strong>not for navigation</strong>. Washington law requires vessels stay{' '}
        <strong>1000 yards</strong> from Southern Resident killer whales.{' '}
        <a href="https://www.bewhalewise.org/" target="_blank" rel="noopener noreferrer">
          Be Whale Wise
        </a>
        . Data from{' '}
        <a href="https://acartia.io" target="_blank" rel="noopener noreferrer">
          Acartia
        </a>{' '}
        and Orca Network. View points from Orca Network and the community. Map data ©{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
          OpenStreetMap contributors
        </a>
        , tiles by{' '}
        <a href="https://openfreemap.org" target="_blank" rel="noopener noreferrer">
          OpenFreeMap
        </a>{' '}
        &amp;{' '}
        <a href="https://openmaptiles.org" target="_blank" rel="noopener noreferrer">
          OpenMapTiles
        </a>
        .
      </div>
    </div>
  );
}
